import { omit } from 'lodash-es';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH' | 'OPTIONS' | 'HEAD' | 'TRACE' | 'CONNECT';

export class ResponseError extends Error {
  public statusCode: number;
  public response: string;

  constructor(statusCode: number, response: string, message?: string) {
    super(message || `Request failed with status ${statusCode}: ${response}`);
    this.statusCode = statusCode;
    this.response = response;
  }
}

export interface FetcherParams<U> {
  url: string;
  method: HttpMethod;
  data?: U;
  params?: U;
  baseUrl?: null | string;
  headers?: Headers;
  timeout?: number;
  withCredentials?: boolean;
  cache?: RequestCache;
}

const DEFAULT_CONFIG = {
  timeout: 10 * 1000,
  cache: 'no-store',
  credentials: true,
} as const;

const createCookieString = (headers: Headers | undefined) => {
  if (!headers) return '';

  let cookieString = '';
  const nextCookies = headers.get('cookie') || '';
  const sidKey = 'yanolja_sid';

  // 쿠키를 문자열로 변환
  nextCookies
    .toString()
    .split(';')
    .forEach((cookie) => {
      const [key] = cookie.split('=');
      if (key === sidKey) {
        cookieString += `${key}=${encodeURIComponent(headers.get(sidKey) || '')};`;
      } else {
        cookieString += `${cookie};`;
      }
    });

  return cookieString;
};

// 타임아웃 Promise 생성 (signal 사용 금지)
const createTimeoutPromise = (timeout: number) =>
  new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error('Request timeout'));
    }, timeout);
  });

// 응답 데이터 JSON 파싱
const parseResponse = async <T>(response: Response): Promise<T> => {
  try {
    return (await response.json()) as T; // JSON 파싱 시도
  } catch {
    return null as T; // 실패 시 null 반환
  }
};

// fetch 요청 메서드
export const fetcher = async <T, U = never>({
  url,
  method,
  data,
  params,
  headers,
  baseUrl = null,
  timeout = DEFAULT_CONFIG.timeout,
  withCredentials = DEFAULT_CONFIG.credentials,
  cache = DEFAULT_CONFIG.cache,
}: FetcherParams<U>): Promise<T> => {
  const isServer = typeof window === 'undefined';
  const headerList = headers ? Object.fromEntries(headers?.entries()) : {};
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(headers ? { platform: headers.get('platform-type') || '' } : {}), // 플랫폼 타입 헤더
    ...(isServer ? omit(headerList, ['Connection', 'connection', 'content-type']) : headerList), // [주의] 안드로이드 앱에서 해더에 'content-type' 가 포함되어 'Content-Type' 와 중복되어 400 에러 발생하여 제거
    ...(isServer ? { Cookie: createCookieString(headers) } : ''),
  };
  const options: RequestInit = {
    method,
    headers: requestHeaders,
    body: data ? JSON.stringify(data) : undefined,
    cache,
    ...(withCredentials && { credentials: 'include' }),
  };

  let apiUrl = '';
  if (baseUrl === null) {
    apiUrl = isServer ? `${process.env.NEXT_PUBLIC_FE_API_ORIGIN}/api/v2` : '/api/v2';
  } else {
    apiUrl = baseUrl;
  }

  const urlWithParams = params ? `${apiUrl}${url}?${new URLSearchParams(params).toString()}` : `${apiUrl}${url}`;

  try {
    // fetch 요청 및 타임아웃 병렬 실행
    const fetchPromise = fetch(urlWithParams, options);
    const timeoutPromise = createTimeoutPromise(timeout);

    // 요청과 타임아웃 중 먼저 완료된 Promise 반환
    const response = await Promise.race([fetchPromise, timeoutPromise]);

    if (!response.ok) {
      const errorMessage = await response.text();
      const error = new ResponseError(response.status, errorMessage);

      console.error('Error fetching data', {
        url,
        status: response.status,
        error: errorMessage,
        params,
        method,
      });

      throw error;
    }

    return parseResponse<T>(response);
  } catch (error) {
    console.error('fetcher error', {
      url,
      method,
      params,
      error: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
    });

    throw error;
  }
};
